import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { DatabaseMapping, MappingVersionInfo, UpgradeResult } from '../../models/database-mapping.model';

@Component({
  selector: 'app-database-mappings',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './database-mappings.component.html',
  styleUrl: './database-mappings.component.scss'
})
export class DatabaseMappingsComponent implements OnInit {
  mappings: DatabaseMapping[] = [];
  availableSourceDatabases: string[] = [];
  availableTargetDatabases: string[] = [];
  loading: boolean = false;
  error: string = '';

  // Form state
  showAddForm: boolean = false;
  editingMapping: DatabaseMapping | null = null;

  // New mapping form
  newMapping: Partial<DatabaseMapping> = {
    sourceDatabase: '',
    targetDatabase: '',
    description: '',
    isActive: true
  };

  // Delete confirmation
  showDeleteModal: boolean = false;
  mappingToDelete: DatabaseMapping | null = null;

  // Version info state
  versionInfoMap: Map<number, MappingVersionInfo> = new Map();
  loadingVersionInfo: Set<number> = new Set();
  expandedMapping: number | null = null;

  // Upgrade confirmation modal
  showUpgradeModal: boolean = false;
  mappingToUpgrade: DatabaseMapping | null = null;
  upgrading: boolean = false;
  upgradeResult: UpgradeResult | null = null;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.loadMappings();
    this.loadAvailableDatabases();
  }

  loadMappings(): void {
    this.loading = true;
    this.apiService.getDatabaseMappings().subscribe({
      next: (mappings) => {
        this.mappings = mappings;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Failed to load mappings: ' + err.message;
        this.loading = false;
      }
    });
  }

  loadAvailableDatabases(): void {
    this.apiService.getSourceDatabasesForMapping().subscribe({
      next: (databases) => {
        this.availableSourceDatabases = databases;
      },
      error: (err) => {
        console.error('Failed to load source databases:', err);
      }
    });

    this.apiService.getTargetDatabasesForMapping().subscribe({
      next: (databases) => {
        this.availableTargetDatabases = databases;
      },
      error: (err) => {
        console.error('Failed to load target databases:', err);
      }
    });
  }

  openAddForm(): void {
    this.showAddForm = true;
    this.editingMapping = null;
    this.newMapping = {
      sourceDatabase: '',
      targetDatabase: '',
      description: '',
      isActive: true
    };
  }

  openEditForm(mapping: DatabaseMapping): void {
    this.showAddForm = true;
    this.editingMapping = mapping;
    this.newMapping = { ...mapping };
  }

  cancelForm(): void {
    this.showAddForm = false;
    this.editingMapping = null;
    this.newMapping = {
      sourceDatabase: '',
      targetDatabase: '',
      description: '',
      isActive: true
    };
  }

  saveMapping(): void {
    if (!this.newMapping.sourceDatabase || !this.newMapping.targetDatabase) {
      this.error = 'Please select both source and target databases';
      return;
    }

    if (this.newMapping.sourceDatabase === this.newMapping.targetDatabase) {
      this.error = 'Source and target databases must be different';
      return;
    }

    this.error = '';

    if (this.editingMapping) {
      // Update existing
      const updated: DatabaseMapping = {
        ...this.editingMapping,
        ...this.newMapping
      } as DatabaseMapping;

      this.apiService.updateDatabaseMapping(updated.mappingID, updated).subscribe({
        next: () => {
          this.loadMappings();
          this.cancelForm();
        },
        error: (err) => {
          this.error = 'Failed to update mapping: ' + (err.error?.message || err.message);
        }
      });
    } else {
      // Create new
      this.apiService.createDatabaseMapping(this.newMapping).subscribe({
        next: () => {
          this.loadMappings();
          this.cancelForm();
        },
        error: (err) => {
          this.error = 'Failed to create mapping: ' + (err.error?.message || err.message);
        }
      });
    }
  }

  confirmDelete(mapping: DatabaseMapping): void {
    this.mappingToDelete = mapping;
    this.showDeleteModal = true;
  }

  cancelDelete(): void {
    this.mappingToDelete = null;
    this.showDeleteModal = false;
  }

  executeDelete(): void {
    if (!this.mappingToDelete) return;

    this.apiService.deleteDatabaseMapping(this.mappingToDelete.mappingID).subscribe({
      next: () => {
        this.loadMappings();
        this.cancelDelete();
      },
      error: (err) => {
        this.error = 'Failed to delete mapping: ' + err.message;
        this.cancelDelete();
      }
    });
  }

  // Version Info Methods
  toggleVersionInfo(mapping: DatabaseMapping): void {
    if (this.expandedMapping === mapping.mappingID) {
      this.expandedMapping = null;
    } else {
      this.expandedMapping = mapping.mappingID;
      this.loadVersionInfo(mapping.mappingID);
    }
  }

  loadVersionInfo(mappingId: number): void {
    if (this.loadingVersionInfo.has(mappingId)) return;

    this.loadingVersionInfo.add(mappingId);
    this.apiService.getMappingVersionInfo(mappingId).subscribe({
      next: (info) => {
        this.versionInfoMap.set(mappingId, info);
        this.loadingVersionInfo.delete(mappingId);
      },
      error: (err) => {
        console.error('Failed to load version info:', err);
        this.loadingVersionInfo.delete(mappingId);
      }
    });
  }

  refreshVersionInfo(mappingId: number): void {
    this.versionInfoMap.delete(mappingId);
    this.loadVersionInfo(mappingId);
  }

  getVersionInfo(mappingId: number): MappingVersionInfo | undefined {
    return this.versionInfoMap.get(mappingId);
  }

  isLoadingVersionInfo(mappingId: number): boolean {
    return this.loadingVersionInfo.has(mappingId);
  }

  // Upgrade Methods
  confirmUpgrade(mapping: DatabaseMapping): void {
    this.mappingToUpgrade = mapping;
    this.showUpgradeModal = true;
    this.upgradeResult = null;
  }

  cancelUpgrade(): void {
    this.mappingToUpgrade = null;
    this.showUpgradeModal = false;
    this.upgradeResult = null;
  }

  executeUpgrade(): void {
    if (!this.mappingToUpgrade) return;

    this.upgrading = true;
    this.upgradeResult = null;

    this.apiService.upgradeTargetDatabase(this.mappingToUpgrade.mappingID).subscribe({
      next: (result) => {
        this.upgradeResult = result;
        this.upgrading = false;
        if (result.success) {
          // Refresh version info after successful upgrade
          this.refreshVersionInfo(this.mappingToUpgrade!.mappingID);
        }
      },
      error: (err) => {
        this.upgradeResult = {
          success: false,
          message: 'Failed to upgrade: ' + (err.error?.message || err.message),
          versionsUpgraded: 0,
          logsApplied: 0,
          steps: []
        };
        this.upgrading = false;
      }
    });
  }
}

